import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Check, X, FileText, Mail } from "lucide-react";
import { cn } from "@shared/lib/utils";

interface ApproveSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  stats: {
    billCount: number;
    emailCount: number;
  };
}

export const ApproveSendModal = ({
  isOpen,
  onClose,
  onConfirm,
  stats,
}: ApproveSendModalProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[460px] p-0 overflow-hidden border-none shadow-2xl rounded-2xl">
        {/* Header */}
        <DialogHeader className="p-4 border-b border-neutral-100 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            <DialogTitle className="text-base font-bold text-neutral-800">
              Approve & Send Bills
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="p-6 flex flex-col items-center text-center space-y-5">
          {/* Hero Icon */}
          <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
            <div className="w-11 h-11 bg-green-100/50 rounded-full flex items-center justify-center">
              <FileText className="w-6 h-6 text-green-600" />
            </div>
          </div>

          <div className="space-y-1">
            <h3 className="text-xl font-bold text-neutral-900 tracking-tight">
              Ready to Approve & Send
            </h3>
            <p className="text-neutral-500 text-xs">
              You are about to approve and send final bills to consumers.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 w-full">
            <div className="p-4 rounded-xl border border-neutral-300 flex flex-col items-center justify-center space-y-0.5">
              <span className="text-2xl font-bold text-blue-600">
                {stats.billCount}
              </span>
              <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider">
                Bills Generated
              </span>
            </div>
            <div className="p-4 rounded-xl border border-neutral-300 flex flex-col items-center justify-center space-y-0.5">
              <span className="text-2xl font-bold text-green-600">
                {stats.emailCount}
              </span>
              <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider">
                Will Receive E-Bills
              </span>
            </div>
          </div>

          {/* Info Box */}
          <div className="w-full p-3 bg-blue-50/50 border border-blue-100 rounded-xl flex items-center gap-3">
            <div className="w-7 h-7 bg-blue-100 rounded flex items-center justify-center shrink-0">
              <Mail className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <p className="text-[11px] font-medium text-blue-700 text-left leading-tight">
              E-Bills will be sent to consumers via email and SMS notification.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 pt-0 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="h-10 px-6 rounded-xl font-bold text-neutral-600 border-neutral-200 hover:bg-neutral-50 transition-all text-xs"
          >
            Cancel
          </Button>
          <Button
            className="bg-green-700 hover:bg-green-800 h-10 px-6 rounded-xl font-bold gap-2 text-white shadow-lg shadow-green-100 transition-all active:scale-[0.98] text-xs"
            onClick={onConfirm}
          >
            <Check className="w-3.5 h-3.5" />
            Approve & Send
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
